import { formatCurrency, formatNumber as displayNumber } from '@/helpers/display-format'

/**
 * This is one of the easiest & proven way to check if a string, number or float is numberic.
 * @source https://stackoverflow.com/questions/175739/built-in-way-in-javascript-to-check-if-a-string-is-a-valid-number
 */
export function isNumeric(str) {
  if (typeof str === 'number') return true

  if (!str && (str !== '0' || str !== '0.00')) return false

  if (typeof str === 'string' && str?.includes(',')) {
    str = str?.replace('.', '')?.replace(',', '.')
  }

  // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this) and ensure strings of whitespace fail
  return Boolean(!isNaN(str) && !isNaN(parseFloat(str)))
}

export const formatNumber = n => n && typeof n === 'string' && n?.includes(',') ? n.replace('.', '').replace(',', '.') : n

export const numWithDefaultZero = (n) => {
  if (n && typeof n === 'string' && n?.includes(',')) {
    n = n.replace('.', '').replace(',', '.')
  }

  return isNaN(parseFloat(n)) ? 0.00 : parseFloat(n)
}

export const currencyFormat = (val: number | null) => formatCurrency(val ?? 0)

export function dutchNumberFormat(num) {
  return num ? displayNumber(num) : undefined
}

/**
 * Handles rounding of numbers for the given precision.
 * Given cases are desired if roundBy is 100
 * case 1: 8.25 x €288.86 = 2383.0950000000003 -> 2383.10
 * case 2: 22.06 x €0.75 = 16.544999999999998 -> 16,55
 * case 3: 0.1000 x €12.234 = 1.2234 -> 1.22
 * case 4; 1.00024 x €10 = 10.0024 -> 10,00
 * case 5: 10.123 x €5.432 = 54.988136 -> 54.99
 * case 6: 4.735 x €5.432 = 25.720520000000004 -> 25.72
 * case 7: 1.00024 x €12.23 = 12.2329352 -> 12,23
 */
export const rounded = (value, roundBy = 100) => {
  // Calculate the number of decimal places based on roundBy
  const decimalPlaces: number = Math.log10(roundBy)
  const factor: number = Math.pow(10, decimalPlaces)
  const valueIsNegative = Math.sign(value) === -1

  // Number.EPSILON is too small and causes problems in case 2
  // If the rounding goes wrong far after the decimal point in the future, we could choose to increase this like 1e-8
  const epsilon = 1e-10

  // Make the value positive to avoid rounding errors
  if (valueIsNegative) value = Math.abs(value)

  // Add a very small value to avoid rounding errors
  value += epsilon

  // Multiply and divide by the factor to round to the desired precision
  const result = Math.round(value * factor) / factor

  // Return the result either positive or negative based on the original value
  return valueIsNegative ? result * -1 : result
}

export const generateRandomKey = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export const convertDecimalToMinutes = (value: any) => {
  if (value === null) return 0.00

  value = parseFloat(value.toString().replace(',', '.'))

  const hours = Math.floor(value)
  const minutes = Math.round((value - hours) * 60)

  return (hours * 60) + minutes
}
